import React, { useEffect, useState, useMemo, useCallback } from 'react'
import {
  ButtonV2,
  VerticalDivider,
  Filters,
  SectionProps,
  CheckBoxFieldv2,
  DateRangePickerV2,
  CustomRangeObject,
  FiltersApplied,
  ModalConfirmV2,
  ModalSizeVariants,
} from '@labrav/react-components'
import { isValid, format } from 'date-fns'
import { useDispatch, useSelector } from '../../../store/hooks'
import {
  UsageDataState,
  UsageFilterState,
} from '../../../modules/usageMetering/types'
import {
  cloudOptions,
  usageStatusMap,
} from '../../../modules/usageMetering/constants'
import {
  customDateRanges,
  searchDropDownOptions,
  statusUsage,
} from '../../../common/utils/meteringConstants'
import { getRangeDates, stripTime } from '../../../common/utils/getDateRanges'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDeleteRight } from '@fortawesome/pro-regular-svg-icons'
import {
  convertToFullDateTime,
  formatDateToISO,
} from '../../../common/utils/meteringHelpers'
import { DateTime } from 'luxon'
import { useStyles } from './UsageMeteringFilters.styles'
import {
  clearUsageMeteringData,
  setFilters,
  clearFilters,
} from '../../../modules/usageMetering/action'
import { useScreenSize } from '@labrav/common'
import { DownloadMeteringHistory } from '../DownloadMeteringHistory/DownloadMeteringHistory'
import { SearchBoxComponent } from '@labrav/react-components'
import { LoadingTypes } from '@labrav/common'
import { useFlagValue } from '@labrav/flags'
import { CloudAccountChangeV2, CloudAccountChangeParams } from '@labrav/common'
import {
  useAccountId,
  useCloudAccounts,
  extractAccountIdFromCombinedId,
  getSelectedAccountIdWithCloud,
} from '@labrav/common'
import { useCurrentCloudProvider } from '@labrav/common'
import { useNavigate, useParams } from 'react-router-dom'
import { setSelectedAccount } from '@labrav/common'
import { CloudType } from '@labrav/common'
import { startLoading, stopLoading } from '@labrav/common'
import { actionTypeWrapper } from '@labrav/common'
import { setSelectedProductOnListing } from '../../../modules/productsListing/action'
import { setCloudMarketplaceProductListing } from '../../../modules/productListingFilter/action'
import { CloudMarketplace } from '../../../modules/productsListing/types'

export interface Range {
  startDate: Date
  endDate: Date
}
const noOps = () => void 0

interface UsageMeteringFiltersProps {
  resetPagenumber: () => void
  updateMeteringModalVisibility: () => void
  handleSearchClick?: () => void
  searchNow?: boolean
  setSearchNow?: React.Dispatch<React.SetStateAction<boolean>>
  showCrossMark?: boolean
  setShowCrossMark?: React.Dispatch<React.SetStateAction<boolean>>
  inputValue?: string
  setInputValue?: React.Dispatch<React.SetStateAction<string>>
  selectedOption?: string
  setSelectedOption?: React.Dispatch<React.SetStateAction<string>>
  count: number
}

export const UsageMeteringHeaders: React.FC<UsageMeteringFiltersProps> = ({
  updateMeteringModalVisibility,
  resetPagenumber,
  handleSearchClick = noOps,
  searchNow = false,
  setSearchNow = noOps,
  showCrossMark = false,
  setShowCrossMark = noOps,
  inputValue = '',
  setInputValue = noOps,
  selectedOption = '',
  setSelectedOption = noOps,
  count,
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const accountId = useAccountId({ product: 'flyout' })
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const currentAccountId = routeAccountId || accountId

  const baseMarketplaceCloudAccounts = useCloudAccounts('flyout')

  const marketplaceCloudAccounts = useMemo(
    () =>
      baseMarketplaceCloudAccounts.map(account => ({
        ...account,
        disabled: account.provider == 'GCP', // Disable GCP accounts since usage metering is not supported for GCP
      })),
    [baseMarketplaceCloudAccounts]
  )

  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    currentAccountId,
    'flyout',
    routeCloud || selectedCloudTypeProductListing
  )

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      const originalAccountId = extractAccountIdFromCombinedId(params.accountId)
      const { cloud } = params

      const cloudPath = cloud.toLowerCase()
      const pathname = `/flyout/${cloudPath}/${originalAccountId}/usage-metering`
      navigate(pathname, { replace: true })

      await dispatch(startLoading(LoadingTypes.GENERAL))

      await dispatch(
        setSelectedAccount(originalAccountId, 'flyout', cloud as CloudType)
      )

      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(setCloudMarketplaceProductListing(cloud))

      setInputValue('')
      setShowCrossMark(false)
      setSearchNow(false)
      resetPagenumber()

      await dispatch(clearUsageMeteringData())

      await dispatch(stopLoading(LoadingTypes.GENERAL))
    },
    [
      dispatch,
      navigate,
      selectedCloudTypeProductListing,
      resetPagenumber,
      setInputValue,
      setShowCrossMark,
      setSearchNow,
    ]
  )

  const today = new Date()
  const { pageSize: pageSizeValue } = useScreenSize()
  const { data, filters } = useSelector<UsageDataState>(
    state => state.usageData
  )
  const { dateRangeFilter } = filters

  const cloudMarketplace: CloudMarketplace = routeCloud
    ? (routeCloud.toUpperCase() as CloudMarketplace)
    : selectedCloudTypeProductListing

  const [selectedUsageStatusCheckboxes, setSelectedUsageStatusCheckboxes] =
    useState<string[]>(filters.status || [])
  const [selectedUsageCloudCheckboxes, setSelectedUsageCloudCheckboxes] =
    useState<string[]>(filters.cloud || [])
  const [appliedFilteredValues, setAppliedFilteredValues] = useState<string[]>([
    ...Object.keys(usageStatusMap).filter(status =>
      filters.status.includes(usageStatusMap[status])
    ),
    ...Object.keys(cloudOptions).filter(title =>
      filters.cloud.includes(cloudOptions[title])
    ),
  ])
  const [showClearFiltersModal, setShowClearFiltersModal] = useState(false)

  const handleClearFiltersClick = useCallback(() => {
    setShowClearFiltersModal(true)
  }, [])
  const handleConfirmClearFilters = useCallback(async () => {
    await dispatch(clearUsageMeteringData())
    dispatch(clearFilters())
    resetPagenumber()
    setShowClearFiltersModal(false)
  }, [dispatch, resetPagenumber])
  const handleDiscardClearFilters = useCallback(() => {
    setShowClearFiltersModal(false)
  }, [])

  useEffect(() => {
    setSelectedUsageStatusCheckboxes(filters.status || [])
    setSelectedUsageCloudCheckboxes(filters.cloud || [])
    setAppliedFilteredValues([
      ...Object.keys(usageStatusMap).filter(status =>
        filters.status?.includes(usageStatusMap[status])
      ),
      ...Object.keys(cloudOptions).filter(title =>
        filters.cloud?.includes(cloudOptions[title])
      ),
    ])
  }, [filters])

  const { startDate = '', endDate = '' } = dateRangeFilter ?? {}
  const formattedStartDate = useMemo(() => {
    return convertToFullDateTime(startDate) as unknown as Date
  }, [startDate])

  const formattedEndDate = useMemo(() => {
    return convertToFullDateTime(endDate) as unknown as Date
  }, [endDate])

  const selectedStatusCheckboxLabels = useMemo<string[]>(
    () =>
      Object.keys(usageStatusMap).filter(key =>
        selectedUsageStatusCheckboxes.includes(usageStatusMap[key])
      ),
    [selectedUsageStatusCheckboxes]
  )
  const selectedCloudCheckboxLabels = useMemo<string[]>(
    () =>
      Object.keys(cloudOptions).filter(key =>
        selectedUsageCloudCheckboxes.includes(cloudOptions[key])
      ),
    [selectedUsageCloudCheckboxes]
  )

  const handleStatusCheckboxChange =
    (title: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target
      const valueToAddOrRemove = usageStatusMap[title]

      setSelectedUsageStatusCheckboxes(prev => {
        const currentSet = new Set(prev)
        if (checked) {
          currentSet.add(valueToAddOrRemove)
        } else {
          currentSet.delete(valueToAddOrRemove)
        }

        return Array.from(currentSet)
      })
    }
  const handleCloudCheckboxChange =
    (title: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target
      const valueToAddOrRemove = cloudOptions[title]

      setSelectedUsageCloudCheckboxes(prev => {
        const currentSet = new Set(prev)
        if (checked) {
          currentSet.add(valueToAddOrRemove)
        } else {
          currentSet.delete(valueToAddOrRemove)
        }

        return Array.from(currentSet)
      })
    }

  const handleDeleteStatusCheckbox = (title: keyof typeof usageStatusMap) => {
    const valueToRemove = usageStatusMap[title]
    setSelectedUsageStatusCheckboxes(prev =>
      prev.filter(item => item !== valueToRemove)
    )
  }
  const handleDeleteCloudCheckbox = (title: keyof typeof cloudOptions) => {
    const valueToRemove = cloudOptions[title]
    setSelectedUsageCloudCheckboxes(prev =>
      prev.filter(item => item !== valueToRemove)
    )
  }
  const formatRangeLabel = (label: string, startDate: Date, endDate: Date) => (
    <div className={classes.labelContent}>
      <span className={classes.labelContentTitle}>{label}</span>
      <Typography className={classes.labelSubTitle}>
        {isValid(startDate) ? format(startDate, 'MMM dd') : 'Invalid date'} -
        {isValid(endDate) ? format(endDate, 'MMM dd') : 'Invalid date'}
      </Typography>
    </div>
  )
  const customRanges: CustomRangeObject[] = customDateRanges.map(label => {
    const { startDate, endDate } = getRangeDates(
      label,
      today
      //will uncoment when get the start date
      //insertDates?.invoiceDate
    )
    return {
      label: formatRangeLabel(label, startDate, endDate),
      range: () => ({ startDate, endDate }),
      isSelected(range: Range) {
        const { startDate, endDate } = getRangeDates(
          label,
          today
          //   insertDates?.invoiceDate //uncomment when write the reducer
        )
        return (
          stripTime(range.startDate).getTime() ===
            stripTime(startDate).getTime() &&
          stripTime(range.endDate).getTime() === stripTime(endDate).getTime()
        )
      },
    }
  })
  const handleDateRangeChange = async (range: Range) => {
    const startDateIso = formatDateToISO(range.startDate)
    const endDateIso = formatDateToISO(range.endDate)
    const filters: UsageFilterState = {
      status: selectedUsageStatusCheckboxes || [],
      // cloud: selectedUsageCloudCheckboxes || [],
      cloud: [],
      dateRangeFilter: { startDate: startDateIso, endDate: endDateIso },
    }
    await dispatch(setFilters(filters))
  }
  const handleDeleteDateRangeCheckbox = async () => {
    const startDate = DateTime.now().minus({ months: 1 }).toISODate() as string
    const endDate = DateTime.now().toISODate() as string
    const filters: UsageFilterState = {
      status: selectedUsageStatusCheckboxes || [],
      // cloud: selectedUsageCloudCheckboxes || [],
      cloud: [],
      dateRangeFilter: { startDate: startDate, endDate: endDate },
    }
    await dispatch(setFilters(filters))
  }

  const sections: SectionProps[] = [
    // {
    //   title: 'Cloud',
    //   type: 'checkbox',
    //   content: (
    //     <div
    //       data-testid="private-offer-status-checkbox"
    //       className={classes.filterSection}
    //     >
    //       {Object.keys(cloudOptions).map(title => (
    //         <CheckBoxFieldv2
    //           key={title}
    //           keyName="cloud"
    //           title={title}
    //           value={selectedUsageCloudCheckboxes.includes(cloudOptions[title])}
    //           onChangeBox={handleCloudCheckboxChange(title)}
    //           disabled={disabledCloud.includes(title)}
    //         />
    //       ))}
    //     </div>
    //   ),
    //   selectedValues: selectedCloudCheckboxLabels,
    //   onDelete: handleDeleteCloudCheckbox,
    // },
    {
      title: 'Status',
      type: 'checkbox',
      content: (
        <div
          data-testid="private-offer-status-checkbox"
          className={classes.filterSection}
        >
          {Object.keys(usageStatusMap).map(title => (
            <CheckBoxFieldv2
              key={title}
              keyName="usageStatus"
              title={title}
              value={selectedUsageStatusCheckboxes.includes(
                usageStatusMap[title]
              )}
              onChangeBox={handleStatusCheckboxChange(title)}
            />
          ))}
        </div>
      ),
      selectedValues: selectedStatusCheckboxLabels,
      onDelete: handleDeleteStatusCheckbox,
    },
  ]

  const applyFilters = useCallback(async () => {
    await dispatch(clearUsageMeteringData())
    const filters: UsageFilterState = {
      status: selectedUsageStatusCheckboxes || [],
      // cloud: selectedUsageCloudCheckboxes || [],
      cloud: [],
      dateRangeFilter: dateRangeFilter,
    }
    await dispatch(setFilters(filters))
    resetPagenumber()
    setAppliedFilteredValues([
      ...Object.keys(usageStatusMap).filter(status =>
        selectedUsageStatusCheckboxes.includes(usageStatusMap[status])
      ),
      // ...Object.keys(cloudOptions).filter(title =>
      //   selectedUsageCloudCheckboxes.includes(cloudOptions[title])
      // ),
    ])
  }, [
    selectedUsageStatusCheckboxes,
    // selectedUsageCloudCheckboxes,
    dateRangeFilter,
  ])
  const onDeleteFiltersAppliedChip = useCallback(
    async (value: string) => {
      // if (cloudArray.includes(value)) {
      //   setSelectedUsageCloudCheckboxes(prev => {
      //     const updatedCloudCheckboxes = prev.filter(
      //       cloud => cloud !== cloudOptions[value]
      //     )
      //     dispatch(clearUsageMeteringData())
      //     resetPagenumber()
      //     const filters: UsageFilterState = {
      //       status: selectedUsageStatusCheckboxes || [],
      //       cloud: updatedCloudCheckboxes || [],
      //       dateRangeFilter: dateRangeFilter,
      //     }
      //     dispatch(setFilters(filters))
      //     setAppliedFilteredValues([
      //       ...Object.keys(usageStatusMap).filter(status =>
      //         selectedUsageStatusCheckboxes.includes(usageStatusMap[status])
      //       ),
      //       ...Object.keys(cloudOptions).filter(title =>
      //         updatedCloudCheckboxes.includes(cloudOptions[title])
      //       ),
      //     ])
      //     return updatedCloudCheckboxes
      //   })
      // } else
      if (statusUsage.includes(value)) {
        setSelectedUsageStatusCheckboxes(prev => {
          const updatedStatusCheckboxes = prev.filter(
            status => status !== usageStatusMap[value]
          )
          dispatch(clearUsageMeteringData())
          resetPagenumber()
          const filters: UsageFilterState = {
            status: updatedStatusCheckboxes || [],
            // cloud: selectedUsageCloudCheckboxes || [],
            cloud: [],
            dateRangeFilter: dateRangeFilter,
          }
          dispatch(setFilters(filters))

          setAppliedFilteredValues([
            ...Object.keys(usageStatusMap).filter(status =>
              updatedStatusCheckboxes.includes(usageStatusMap[status])
            ),
            // ...Object.keys(cloudOptions).filter(title =>
            //   selectedUsageCloudCheckboxes.includes(cloudOptions[title])
            // ),
          ])
          return updatedStatusCheckboxes
        })
      }
    },
    [
      appliedFilteredValues,
      selectedUsageStatusCheckboxes,
      dateRangeFilter,
      dispatch,
    ]
  )

  return (
    <div>
      <div className={classes.contentWrapper} data-testid="header-menu">
        <div>
          {routeCloud !== 'azure' && (
            <SearchBoxComponent
              options={searchDropDownOptions.meteringOptions}
              handleSearchClick={handleSearchClick}
              loading={false}
              searchNow={searchNow}
              setSearchNow={setSearchNow}
              showCrossMark={showCrossMark}
              setShowCrossMark={setShowCrossMark}
              inputValue={inputValue}
              setInputValue={setInputValue}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
            />
          )}
        </div>
        <div className={classes.headingWrapper}>
          <div style={{ width: '220px', height: '44px' }}>
            <CloudAccountChangeV2
              cloudAccounts={marketplaceCloudAccounts}
              selectedAccountId={selectedAccountIdWithCloud}
              onChange={handleCloudAccountChange}
              width={220}
              size="small"
            />
          </div>
          <VerticalDivider />
          <div
            className={classes.dateRangePickerHeader}
            data-testid="date-range-picker-header"
          >
            <DateRangePickerV2
              keyName="date-range-calander-header"
              title="Date range picker"
              onDateRangeChange={handleDateRangeChange}
              customRanges={customRanges}
              initialRange={{
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                key: 'selection',
              }}
            />
          </div>
          <VerticalDivider />
          <ButtonV2
            data-testid="create-metering-record"
            styleType="primary"
            onClick={updateMeteringModalVisibility}
            disabled={false}
          >
            Create metering
          </ButtonV2>
          <VerticalDivider />
          <DownloadMeteringHistory />
          <VerticalDivider />
          <div className={classes.filters}>
            <Filters
              sections={sections}
              applyFilters={applyFilters}
              clearFilters={handleClearFiltersClick}
              minWidth="400px"
            />
          </div>
        </div>
      </div>
      <div
        className={classes.filtersAppliedContainer}
        data-testid="filters-applied-component"
      >
        <FiltersApplied
          selectedValues={appliedFilteredValues}
          onDelete={onDeleteFiltersAppliedChip}
          chipDisplayLimit={5}
          noBorder={true}
          backgroundColor="#FFFFFF"
        />
      </div>
      <ModalConfirmV2
        variant={ModalSizeVariants.medium}
        open={showClearFiltersModal}
        title="Clear filters"
        description=""
        titleAcceptButton="Yes"
        titleCancelButton="Discard"
        onAccept={handleConfirmClearFilters}
        onDecline={handleDiscardClearFilters}
        icon={
          <FontAwesomeIcon
            icon={faDeleteRight}
            data-testid="clear-filters-modal-icon"
          />
        }
        content={
          <Typography variant="body1">
            Are you sure to clear all the filters applied?
          </Typography>
        }
      />
    </div>
  )
}

export interface DateRangeObject {
  startDate: Date
  endDate: Date
  key: string
}

export const convertDateRange = (range: DateRangeObject): string => {
  const startDate = new Date(range.startDate)
  const endDate = new Date(range.endDate)

  const formattedStartDate = format(startDate, 'MMM dd, yy')
  const formattedEndDate = format(endDate, 'MMM dd, yy')

  return `${formattedStartDate} - ${formattedEndDate}`
}
